import { useState, useEffect } from "react";
import { CommonDialog } from "../../../components/common/CommonDialog";
import { DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import {
  FormButtonGroup,
  FormMultipleAutoComplete,
} from "../../../components/form";
import { useForm } from "react-hook-form";
import {
  GetPractitionerRole,
  PatchPractitionerRole,
} from "../../../services/fhir";
import { auxiliarData } from "../../../services/fhir/AuxiliarData";
import { toast } from "react-toastify";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useViewContext } from "../../../contexts/ViewContext";
import { roles as roleCodes } from "../../../configs/Settings";

interface IProps {
  isOpen: boolean;
  handleClose: Function;
  row: any;
  refetchPractitionerRole: any;
  isCurrentUser?: boolean;
  isOwner?: boolean;
}

interface RoleOption {
  label: string;
  value: string;
}

function translateRoles(roles: any) {
  const translationMap: any = {
    owner: "Proprietário(a)",
    manager: "Secretariado",
    practitioner: "Profissional de saúde",
    associated: "Sócio (associação)",
  };

  return roles?.map((role: any) => {
    return { label: translationMap[role] || role, value: role };
  });
}

export function ManagePractitionersDialog({
  isOpen,
  handleClose,
  row,
  refetchPractitionerRole,
  isCurrentUser = false,
  isOwner = false,
}: IProps) {
  const { user } = useSessionContext();
  const { organization } = useOrganizationContext();
  const { availableRoles } = useViewContext();
  const [loading, setLoading] = useState<boolean>(false);

  // Verifica se o usuário atual é proprietário
  const isCurrentUserOwner = availableRoles?.includes("owner");
  
  // Oculta o X apenas quando for o próprio usuário e for proprietário
  const shouldDisableRemove = isCurrentUser && isOwner;
  
  // Verifica se o usuário atual é apenas manager (não tem papel de owner)
  const isCurrentUserOnlyManager = availableRoles?.includes("manager") && !availableRoles?.includes("owner");
  
  // Se o usuário for apenas manager e o profissional sendo editado for owner, desabilita a remoção do papel owner
  const shouldDisableOwnerRemoval = shouldDisableRemove || (isCurrentUserOnlyManager && isOwner);

  // Filtra as opções de papel com base nas permissões do usuário atual.
  // "associated" (RL_ASSOCIATED) não é editável aqui — só exibido quando já existe no recurso.
  const roleOptions = [
    ...(row?.roles?.includes("associated")
      ? [{ label: "Sócio (associação)", value: "associated" }]
      : []),
    ...(isCurrentUserOwner ? [{ label: "Proprietário(a)", value: "owner" }] : []),
    { label: "Secretariado", value: "manager" },
    { label: "Profissional de saúde", value: "practitioner" },
  ]?.sort((el: any, val: any) => (el.label < val.label ? 1 : -1));

  const methods = useForm<{ role: RoleOption[] }>({
    defaultValues: { role: translateRoles(row?.roles) },
  });
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isDirty },
  } = methods;

  const watchPaper = watch("role");

  // Garantir que o papel "owner" não possa ser removido quando necessário
  useEffect(() => {
    if (shouldDisableOwnerRemoval && watchPaper) {
      const hasOwner = watchPaper.some((role: RoleOption) => role.value === "owner");
      if (!hasOwner) {
        // Adiciona o papel "owner" de volta se foi removido
        setValue(
          "role",
          [
            ...watchPaper,
            { label: "Proprietário(a)", value: "owner" },
          ],
          { shouldDirty: false }
        );
      }
    }
  }, [watchPaper, shouldDisableOwnerRemoval, setValue]);

  // Remover opções de papel que o usuário não tem permissão para adicionar
  useEffect(() => {
    if (!isCurrentUserOwner && watchPaper) {
      const hasOwner = watchPaper.some((role: RoleOption) => role.value === "owner");
      if (hasOwner && !isOwner) {
        // Remove o papel "owner" se foi adicionado e o usuário não tem permissão
        setValue(
          "role",
          watchPaper.filter((role: RoleOption) => role.value !== "owner"),
          { shouldDirty: true }
        );
      }
    }
  }, [watchPaper, isCurrentUserOwner, isOwner, setValue]);

  const auxPaper = watchPaper?.map((el: RoleOption) => el);

  const onSubmit = async (data: { role: RoleOption[] }) => {
    try {
      setLoading(true);

      // Papéis editáveis (associated não entra no PATCH de owner/manager/practitioner)
      const rolesSelected = auxPaper
        ?.map((el: RoleOption) => el.value)
        ?.filter((r: string) => r !== "associated");
      let orderDict: { [key: string]: number } = {
        owner: 0,
        manager: 1,
        practitioner: 2,
      };
      let sortedList = rolesSelected?.sort(
        (a: string, b: string) => orderDict[a] - orderDict[b]
      );

      const cnpj = organization?.id?.split("-")[1];

      const verifyPractitioner = await GetPractitionerRole({
        id: `${cnpj}-${row?.practitionerId}`,
      });

      const preserveAssociated = Boolean(
        verifyPractitioner?.code?.some((c: fhir5.CodeableConcept) =>
          c?.coding?.some((coding) => coding?.code === roleCodes.Associated),
        ),
      );

      const patchBody = auxiliarData.PatchPractitionerRole(rolesSelected, {
        preserveAssociated,
      });

      await PatchPractitionerRole({
        practitionerId: `${cnpj}-${row?.practitionerId}`,
        operation: verifyPractitioner?.code ? "replace" : "add",
        path: "/code",
        content: patchBody,
      });

      if (
        sortedList?.[0] === "owner" &&
        rolesSelected.length === 1 &&
        verifyPractitioner?.specialty
      ) {
        await PatchPractitionerRole({
          practitionerId: `${cnpj}-${row?.practitionerId}`,
          operation: "remove",
          path: "/specialty",
        });
      }

      refetchPractitionerRole();
      toast.success("Função atualizada com sucesso!");
      if (row?.practitionerId.includes(user.username)) window.location.reload();
      handleClose();
    } catch (err) {
      console.log("erro:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonDialog handleClose={handleClose} isOpen={isOpen}>
      <DialogTitle textAlign="center">Alterar função</DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          color="neutral700.main"
          textAlign="center"
          mb={2}
        >
          {row?.practitionerName}
        </Typography>
        <FormMultipleAutoComplete
          variant="filled"
          control={control}
          label="Função"
          name="role"
          disableRemoveFor={[
            ...(shouldDisableOwnerRemoval ? ["owner"] : []),
            ...(row?.roles?.includes("associated") ? ["associated"] : []),
          ]}
          selectOptions={roleOptions}
        />

        <Stack
          width="100%"
          direction="column"
          justifyContent="space-between"
          mt={3}
        >
          <FormButtonGroup
            onGoBackButtonClick={handleClose}
            onNextButtonClick={handleSubmit(onSubmit)}
            goBackButtonText="Cancelar"
            nextButtonText="Confirmar"
            disabled={
              !isDirty || 
              (shouldDisableOwnerRemoval && !watchPaper?.some((role: RoleOption) => role.value === "owner"))
            }
            loading={loading}
          />
        </Stack>
      </DialogContent>
    </CommonDialog>
  );
}
